import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'EN', full: 'English' },
  { code: 'bg', label: 'BG', full: 'Български' },
  { code: 'it', label: 'IT', full: 'Italiano' },
  { code: 'ru', label: 'RU', full: 'Русский' },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#111418]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-leaf-line text-green-400 text-2xl"></i>
          </div>
          <span className="text-white font-bold text-lg tracking-tight">CornVest</span>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollTo('why-grows')} className="text-white/70 hover:text-green-400 transition-colors text-sm cursor-pointer whitespace-nowrap">{t('nav.about')}</button>
          <button onClick={() => scrollTo('packages')} className="text-white/70 hover:text-green-400 transition-colors text-sm cursor-pointer whitespace-nowrap">{t('nav.packages')}</button>
          <button onClick={() => scrollTo('calculator')} className="text-white/70 hover:text-green-400 transition-colors text-sm cursor-pointer whitespace-nowrap">{t('nav.calculator')}</button>
          <button onClick={() => scrollTo('activity')} className="text-white/70 hover:text-green-400 transition-colors text-sm cursor-pointer whitespace-nowrap">{t('nav.contacts')}</button>
        </div>

        {/* Right: lang switcher + CTA */}
        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(v => !v)}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/10 hover:border-white/25 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-global-line text-sm"></i>
              <span>{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-[#1a1f1a] border border-white/10 rounded-xl overflow-hidden z-50 min-w-[130px]">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      currentLang.code === lang.code
                        ? 'bg-green-500/15 text-green-400 font-semibold'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="font-bold text-xs w-5">{lang.label}</span>
                    <span>{lang.full}</span>
                    {currentLang.code === lang.code && (
                      <i className="ri-check-line ml-auto text-green-400"></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={() => scrollTo('packages')}
            className="border border-green-400 text-green-400 hover:bg-green-400 hover:text-[#111418] transition-all px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap hidden sm:block"
          >
            {t('nav.cta')}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
